import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { POSTS } from "../../lib/blog";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const BlogIndex = () => {
  useEffect(() => {
    document.title = "Insights & Guides — Empower Legacy Finance";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Australian mortgage guides — FHOG, refinance tips, first home buyer checklists and investment loan strategy from Empower Legacy Finance.");
    window.scrollTo(0, 0);
  }, []);

  const [featured, ...rest] = POSTS;

  return (
    <main data-testid="blog-index" className="font-body bg-brand-cream">
      <Header />

      <section className="pt-32 md:pt-40 pb-16 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="label-eyebrow text-brand-eucalyptus mb-5">
          Insights & guides
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium text-brand-ink leading-[1.02] max-w-4xl">
          Smart Australian mortgage advice — without the jargon.
        </h1>
        <p className="mt-6 max-w-2xl text-base md:text-lg text-brand-ink/70 leading-relaxed">
          Practical guides on first home buying, refinancing, investment loans
          and self-employed lending — written by brokers, for buyers.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-12">
        <Link
          to={`/blog/${featured.slug}`}
          data-testid={`blog-featured-${featured.slug}`}
          className="group grid lg:grid-cols-12 gap-8 rounded-3xl overflow-hidden border border-border bg-card transition-all duration-300 hover:shadow-md"
        >
          <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
            <img
              src={featured.image}
              alt={featured.title}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
            />
          </div>
          <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-brand-eucalyptus/80">
              <span>{featured.category}</span>
              <span className="h-1 w-1 rounded-full bg-brand-ink/30" />
              <span className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" strokeWidth={1.6} />
                {featured.readMinutes} min read
              </span>
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight text-brand-ink group-hover:text-brand-terracotta transition-colors">
              {featured.title}
            </h2>
            <p className="mt-4 text-base text-brand-ink/70 leading-relaxed">
              {featured.excerpt}
            </p>
            <span className="mt-6 inline-flex items-center text-brand-eucalyptus font-medium">
              Read article
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" strokeWidth={1.8} />
            </span>
          </div>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-32">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group rounded-2xl overflow-hidden border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-md flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-brand-eucalyptus/80">
                  <span>{post.category}</span>
                  <span className="h-1 w-1 rounded-full bg-brand-ink/30" />
                  <span>{post.readMinutes} min</span>
                </div>
                <h3 className="mt-4 font-display text-xl tracking-tight text-brand-ink group-hover:text-brand-terracotta transition-colors">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-brand-ink/65 leading-relaxed flex-1">
                  {post.excerpt}
                </p>
                <div className="mt-5 text-xs text-brand-ink/50">
                  {formatDate(post.publishedAt)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
};
